import { Box, Button, Dialog, Stack, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import { useTranslation } from "react-i18next";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { useCustomColors } from "../../hooks/useCustomColors";

/**
 * The moment after registering.
 *
 * It replaces a plain notice box, and the reason is not decoration: this is the
 * only screen between filling in a long form and being told to go away and wait
 * for a human. It should read as a welcome rather than as a receipt, so it
 * leads with the player's own name, shows where they are in a three-step
 * journey, and only then delivers the operator's notice.
 *
 * Every string on it comes from the dictionary, the notice included — which is
 * the point of the `locale` now sent at registration. The dialog used to pair
 * translated chrome with an English sentence from the server, so a player who
 * chose 中文 read half of it in a language they had not asked for.
 *
 * Motion is CSS keyframes rather than a library: the whole sequence is a ring, a
 * tick and a stagger, and none of that is worth a dependency. All of it is
 * disabled under `prefers-reduced-motion` — a celebration nobody asked for is
 * exactly the kind of movement that setting exists to stop.
 */

const popIn = keyframes`
  from { opacity: 0; transform: scale(0.9) translateY(12px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/** The seal lands: overshoots a little, then settles. */
const sealIn = keyframes`
  0%   { opacity: 0; transform: scale(0.4) rotate(-12deg); }
  60%  { opacity: 1; transform: scale(1.12) rotate(4deg); }
  100% { opacity: 1; transform: scale(1) rotate(0deg); }
`;

/** A ring pushed outwards from the seal, twice, so it reads as a pulse. */
const ripple = keyframes`
  0%   { opacity: 0.55; transform: scale(0.85); }
  70%  { opacity: 0; transform: scale(1.7); }
  100% { opacity: 0; transform: scale(1.7); }
`;

/** Sparks drifting up past the seal. */
const drift = keyframes`
  0%   { opacity: 0; transform: translateY(6px) scale(0.6); }
  35%  { opacity: 1; }
  100% { opacity: 0; transform: translateY(-38px) scale(1); }
`;

const shimmer = keyframes`
  from { background-position: -160% 0; }
  to   { background-position: 260% 0; }
`;

/** Where the sparks sit around the seal, and how late each one starts. */
const SPARKS = [
  { left: "6%", delay: "0.45s", size: 6 },
  { left: "24%", delay: "0.75s", size: 4 },
  { left: "52%", delay: "0.35s", size: 5 },
  { left: "78%", delay: "0.85s", size: 4 },
  { left: "92%", delay: "0.6s", size: 6 },
];

export interface SignUpSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  username: string;
  /** The operator's review notice, already in the player's language. */
  notice?: string;
  /** Where to send somebody who wants to chase the review. Hidden when unset. */
  reviewUrl?: string | null;
  /** Primary action — back to login, or across to it inside the mobile dialog. */
  onContinue: () => void;
}

const SignUpSuccessDialog = ({
  open,
  onClose,
  username,
  notice,
  reviewUrl,
  onContinue,
}: SignUpSuccessDialogProps) => {
  const { t } = useTranslation();
  const colors = useCustomColors();

  /*
   * One switch for the whole sequence.
   *
   * Read at render rather than through a media-query hook because the dialog is
   * mounted once per registration and never re-opened, so there is nothing to
   * react to — and a listener here would be a subscription that outlives the one
   * moment it could matter.
   */
  const stillMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  /** Staggered entrance, unless motion is off — then everything is simply there. */
  const step = (index: number) =>
    stillMotion
      ? {}
      : {
          opacity: 0,
          animation: `${riseIn} 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
          animationDelay: `${0.28 + index * 0.09}s`,
        };

  const steps = [
    { icon: CheckRoundedIcon, label: t("sign_up_step_submitted"), state: "done" as const },
    { icon: HourglassTopRoundedIcon, label: t("sign_up_step_review"), state: "active" as const },
    { icon: VerifiedRoundedIcon, label: t("sign_up_step_ready"), state: "todo" as const },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      /* Full width on a phone with breathing room at the edges; a fixed, centred
         card on anything larger. The same component serves H5 and desktop. */
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 32px)", sm: "440px" },
          maxWidth: "100%",
          m: { xs: 2, sm: 3 },
          borderRadius: "22px",
          overflow: "hidden",
          position: "relative",
          backgroundColor: colors.card.dialog,
          backgroundImage: "none",
          border: `1px solid ${colors.border.accent}`,
          boxShadow: colors.card.dialogShadow,
          ...(stillMotion
            ? {}
            : { animation: `${popIn} 0.42s cubic-bezier(0.22, 1, 0.36, 1)` }),
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: colors.tint.scrim,
          backdropFilter: "blur(3px)",
        },
      }}
    >
      {/* ── The celebration ─────────────────────────────────────────────── */}
      <Box
        sx={{
          position: "relative",
          pt: "34px",
          pb: "26px",
          px: { xs: "22px", sm: "30px" },
          textAlign: "center",
          background: colors.gradient.dialogHeader,
          overflow: "hidden",
        }}
      >
        {/* A soft pool of light behind the seal, so the badge sits in something
            rather than on a flat panel. */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "-46%",
            left: "50%",
            transform: "translateX(-50%)",
            width: "320px",
            height: "320px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${colors.tint.goldStrong} 0%, transparent 62%)`,
            pointerEvents: "none",
          }}
        />

        <Box sx={{ position: "relative", display: "inline-flex", mb: "18px" }}>
          {/* Two rings, the second half a beat behind, so the pulse repeats
              without ever looking like a loading spinner. */}
          {!stillMotion &&
            [0, 1].map((ring) => (
              <Box
                key={ring}
                aria-hidden
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: `2px solid ${colors.accent.gold}`,
                  animation: `${ripple} 2.4s ease-out infinite`,
                  animationDelay: `${0.7 + ring * 1.2}s`,
                }}
              />
            ))}

          <Box
            sx={{
              width: "84px",
              height: "84px",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: `linear-gradient(145deg, ${colors.accent.goldBright}, ${colors.accent.gold})`,
              boxShadow: `0 10px 26px rgba(0,0,0,0.35), ${colors.glow.gold}`,
              ...(stillMotion
                ? {}
                : { animation: `${sealIn} 0.62s cubic-bezier(0.34, 1.56, 0.64, 1) both` }),
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: "46px", color: "#2A0708" }} />
          </Box>

          {!stillMotion &&
            SPARKS.map((spark) => (
              <Box
                key={spark.left}
                aria-hidden
                sx={{
                  position: "absolute",
                  bottom: "10%",
                  left: spark.left,
                  width: `${spark.size}px`,
                  height: `${spark.size}px`,
                  borderRadius: "50%",
                  backgroundColor: colors.accent.goldBright,
                  opacity: 0,
                  animation: `${drift} 2.6s ease-out infinite`,
                  animationDelay: spark.delay,
                }}
              />
            ))}
        </Box>

        <Typography
          sx={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: { xs: "21px", sm: "24px" },
            fontWeight: 800,
            fontStyle: "italic",
            lineHeight: 1.25,
            color: "#FFFFFF",
            ...step(0),
          }}
        >
          {t("sign_up_welcome_title", { name: username })}
        </Typography>

        <Typography
          sx={{
            mt: "10px",
            display: "inline-block",
            px: "14px",
            py: "5px",
            borderRadius: "999px",
            fontSize: "10.5px",
            fontWeight: 800,
            letterSpacing: "2.4px",
            color: colors.accent.gold,
            border: `1px solid ${colors.border.strong}`,
            backgroundColor: colors.tint.gold,
            /* A slow sheen across the chip. It is the one thing on screen that
               keeps moving after the entrance, which is what stops a "waiting"
               state from reading as a dead end. */
            ...(stillMotion
              ? {}
              : {
                  backgroundImage: `linear-gradient(100deg, transparent 20%, ${colors.tint.goldStrong} 46%, transparent 72%)`,
                  backgroundSize: "220% 100%",
                  animation: `${shimmer} 3.4s ease-in-out infinite`,
                  animationDelay: "1.1s",
                }),
            ...step(1),
          }}
        >
          {t("sign_up_pending_subtitle")}
        </Typography>
      </Box>

      {/* ── Where they are, and what happens next ───────────────────────── */}
      <Box
        sx={{
          px: { xs: "22px", sm: "30px" },
          py: "22px",
          background: colors.card.dialogBody,
          borderTop: `1px solid ${colors.border.accent}`,
        }}
      >
        <Stack direction="row" sx={{ mb: "20px", ...step(2) }}>
          {steps.map((entry, index) => {
            const Icon = entry.icon;
            const reached = entry.state !== "todo";
            return (
              <Stack
                key={entry.label}
                alignItems="center"
                sx={{ flex: 1, position: "relative", gap: "7px" }}
              >
                {/* The rule joining this step to the one before it. Drawn from
                    the step rather than between them, so the row stays a simple
                    flex of three equal columns at any width. */}
                {index > 0 && (
                  <Box
                    aria-hidden
                    sx={{
                      position: "absolute",
                      top: "13px",
                      right: "50%",
                      width: "100%",
                      height: "2px",
                      backgroundColor: reached
                        ? colors.accent.gold
                        : colors.border.subtle,
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: "relative",
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: reached ? colors.accent.gold : colors.background.field,
                    border: `1px solid ${reached ? colors.accent.gold : colors.border.subtle}`,
                    color: reached ? "#2A0708" : colors.text.disabled,
                    ...(entry.state === "active" && !stillMotion
                      ? { animation: `${ripple} 2.6s ease-out infinite`, animationDelay: "1.4s" }
                      : {}),
                  }}
                >
                  <Icon sx={{ fontSize: "16px" }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "10.5px",
                    fontWeight: entry.state === "active" ? 800 : 600,
                    lineHeight: 1.3,
                    textAlign: "center",
                    color:
                      entry.state === "active"
                        ? colors.text.primary
                        : colors.text.secondary,
                  }}
                >
                  {entry.label}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {/* The operator's own words, in the player's own language. Falls back to
            the dictionary when the setting has been cleared in the console. */}
        <Typography
          sx={{
            fontSize: "13.5px",
            lineHeight: 1.85,
            textAlign: "center",
            color: colors.text.primary,
            ...step(3),
          }}
        >
          {notice?.trim() || t("sign_up_pending_note")}
        </Typography>

        <Box
          sx={{
            mt: "16px",
            px: "16px",
            py: "11px",
            borderRadius: "12px",
            backgroundColor: colors.background.field,
            border: `1px solid ${colors.border.subtle}`,
            ...step(4),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
            <Typography sx={{ fontSize: "12px", color: colors.text.secondary }}>
              {t("sign_up_username")}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 800,
                color: colors.accent.gold,
                wordBreak: "break-all",
              }}
            >
              {username}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* ── What to do now ──────────────────────────────────────────────── */}
      <Stack
        /* Stacked on a phone so neither button is squeezed to two words on two
           lines; side by side once there is room. */
        direction={{ xs: "column-reverse", sm: "row" }}
        sx={{
          px: { xs: "22px", sm: "30px" },
          py: "18px",
          gap: "10px",
          borderTop: `1px solid ${colors.border.accent}`,
          backgroundColor: colors.card.dialogFooter,
          ...step(5),
        }}
      >
        {reviewUrl && (
          <Button
            component="a"
            href={reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="outlinedDark"
            sx={{ height: "50px", borderRadius: "12px", px: "20px", flex: 1 }}
          >
            {t("sign_up_contact_support")}
          </Button>
        )}
        <Button
          onClick={onContinue}
          sx={{ height: "50px", borderRadius: "12px", px: "28px", flex: 1 }}
        >
          {t("sign_up_pending_action")}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default SignUpSuccessDialog;
